import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { authFetch } from '../../context/AuthContext';

/* ── 타입 ── */
interface Receipt  { id: number; receipt_no: string; supplier_name: string; receipt_date: string; total_amount: number; }
interface Shipment { id: number; shipment_no: string; client_name: string;  shipment_date: string; total_amount: number; }
interface ProdStat { status: string; cnt: number; }
interface UserStat { id: number; name: string; email: string; role: string; partner_name: string; is_active: number; last_login: string | null; }
interface Supplier { id: number; company_name: string; is_active: number; }

/* ── 색상 ── */
const PROD_COLORS: Record<string,string> = {
  pending:'#a8a29e', preparing:'#60a5fa', producing:'#f59e0b',
  inspection:'#a855f7', completed:'#22c55e', delayed:'#ef4444',
};
const PROD_LABELS: Record<string,string> = {
  pending:'접수', preparing:'원자재 준비', producing:'생산 중',
  inspection:'검수', completed:'완료', delayed:'지연',
};
const CHART_COLORS = ['#f59e0b','#3b82f6','#22c55e','#a855f7','#ef4444','#06b6d4'];

/* ── 유틸 ── */
const fmt  = (n: number) => n.toLocaleString() + '원';
const today      = new Date().toISOString().split('T')[0];
const monthStart = today.slice(0,7) + '-01';

/* ── CSV ── */
function downloadCSV(filename: string, rows: (string|number)[][], headers: string[]) {
  const bom  = '﻿';
  const body = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
  const blob = new Blob([bom + body], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  Object.assign(document.createElement('a'), { href: url, download: filename }).click();
  URL.revokeObjectURL(url);
}

/* ── KPI 카드 ── */
const KpiCard = ({ label, value, sub, color, icon }:
  { label:string; value:string; sub?:string; color:string; icon:string }) => (
  <div className={`${color} rounded-2xl p-5 shadow-sm`}>
    <div className="flex justify-between items-start mb-3">
      <p className="text-xs font-semibold opacity-60 uppercase tracking-wide">{label}</p>
      <span className="text-2xl">{icon}</span>
    </div>
    <p className="text-2xl font-bold">{value}</p>
    {sub && <p className="text-xs mt-1 opacity-50">{sub}</p>}
  </div>
);

/* ── 섹션 헤더 ── */
const Section = ({ title, sub, action }:
  { title:string; sub?:string; action?:React.ReactNode }) => (
  <div className="flex items-center justify-between mb-4">
    <div>
      <h2 className="text-base font-bold text-stone-800 flex items-center gap-2">
        <span className="w-1 h-5 bg-amber-500 rounded-full inline-block" />
        {title}
      </h2>
      {sub && <p className="text-xs text-stone-400 mt-0.5 pl-3">{sub}</p>}
    </div>
    {action}
  </div>
);

/* ════════════════════════════════════════ */
export default function ReportPage() {
  const [from, setFrom] = useState(monthStart);
  const [to,   setTo]   = useState(today);
  const [inputFrom, setInputFrom] = useState(monthStart);
  const [inputTo,   setInputTo]   = useState(today);

  const [receipts,  setReceipts]  = useState<Receipt[]>([]);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [allReceipts,  setAllReceipts]  = useState<Receipt[]>([]);   // 차트용 6개월 전체
  const [allShipments, setAllShipments] = useState<Shipment[]>([]);
  const [prodStats, setProdStats] = useState<ProdStat[]>([]);
  const [users,     setUsers]     = useState<UserStat[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading,   setLoading]   = useState(true);

  /* ── 6개월 전 날짜 ── */
  const sixMonthsAgo = (() => {
    const d = new Date(); d.setMonth(d.getMonth() - 5);
    return d.toISOString().slice(0,7) + '-01';
  })();

  /* ── 데이터 로드 (날짜 범위 적용) ── */
  const load = useCallback(async (f: string, t: string) => {
    setLoading(true);
    const qs = `from=${f}&to=${t}`;
    const [rr, sr, pr, allR, allS, ur, sup] = await Promise.all([
      authFetch(`/api/receipts?${qs}`).then(r => r.ok ? r.json() : []),
      authFetch(`/api/shipments?${qs}`).then(r => r.ok ? r.json() : []),
      authFetch(`/api/production/summary?${qs}`).then(r => r.ok ? r.json() : {}),
      authFetch(`/api/receipts?from=${sixMonthsAgo}&to=${today}`).then(r => r.ok ? r.json() : []),
      authFetch(`/api/shipments?from=${sixMonthsAgo}&to=${today}`).then(r => r.ok ? r.json() : []),
      authFetch('/api/users').then(r => r.ok ? r.json() : []),
      authFetch('/api/suppliers').then(r => r.ok ? r.json() : []),
    ]);
    setReceipts(rr);
    setShipments(sr);
    setProdStats(Object.entries(pr).map(([status, cnt]) => ({ status, cnt: Number(cnt) })));
    setAllReceipts(allR);
    setAllShipments(allS);
    setUsers(ur);
    setSuppliers(sup);
    setLoading(false);
  }, [sixMonthsAgo]);

  /* 초기 로드 */
  useEffect(() => { load(from, to); }, []);

  /* ── 조회 버튼 ── */
  const handleSearch = () => {
    setFrom(inputFrom);
    setTo(inputTo);
    load(inputFrom, inputTo);
  };

  /* ── KPI ── */
  const totalIn    = receipts.reduce((s, r) => s + r.total_amount, 0);
  const totalOut   = shipments.reduce((s, r) => s + r.total_amount, 0);
  const activeSupp = suppliers.filter(s => s.is_active).length;
  const activeUsers = users.filter(u => u.is_active).length;

  /* ── 월별 추이 (최근 6개월 고정) ── */
  const monthlyData = (() => {
    const months: Record<string,{month:string;입고:number;출고:number}> = {};
    for (let i = 5; i >= 0; i--) {
      const d = new Date(); d.setMonth(d.getMonth() - i);
      const key = d.toISOString().slice(0,7);
      months[key] = { month: key.slice(5)+'월', 입고:0, 출고:0 };
    }
    allReceipts.forEach(r => { const k = r.receipt_date.slice(0,7); if (months[k]) months[k].입고 += r.total_amount; });
    allShipments.forEach(s => { const k = s.shipment_date.slice(0,7); if (months[k]) months[k].출고 += s.total_amount; });
    return Object.values(months);
  })();

  /* ── 거래처별 입고 (기간 내) ── */
  const supplierChart = (() => {
    const map: Record<string,number> = {};
    receipts.forEach(r => { map[r.supplier_name] = (map[r.supplier_name]??0) + r.total_amount; });
    return Object.entries(map).sort((a,b)=>b[1]-a[1]).slice(0,6).map(([name,amount])=>({name,amount}));
  })();

  /* ── 파트너사별 사용자 ── */
  const partnerStats = (() => {
    const map: Record<string,{total:number;active:number}> = {};
    users.forEach(u => {
      if (!map[u.partner_name]) map[u.partner_name] = {total:0,active:0};
      map[u.partner_name].total++;
      if (u.is_active) map[u.partner_name].active++;
    });
    return Object.entries(map).map(([partner,s])=>({name:partner,...s}));
  })();

  const periodLabel = `${from} ~ ${to}`;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">

      {/* ── 헤더 + 기간 필터 ── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">전체 리포트</h1>
          <p className="text-stone-500 text-sm mt-1">입출고 · 생산 · 거래처 · 가입자 현황</p>
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl px-5 py-4 shadow-sm flex flex-wrap items-end gap-4">
          <div>
            <p className="text-xs font-semibold text-stone-500 mb-1.5">조회 시작일</p>
            <input type="date" value={inputFrom} onChange={e => setInputFrom(e.target.value)}
              className="border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
          <div>
            <p className="text-xs font-semibold text-stone-500 mb-1.5">조회 종료일</p>
            <input type="date" value={inputTo} onChange={e => setInputTo(e.target.value)}
              className="border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
          <button onClick={handleSearch} disabled={loading}
            className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold px-5 py-2 rounded-xl text-sm transition h-[38px]">
            {loading ? '조회 중...' : '조회'}
          </button>
          <button onClick={() => { setInputFrom(monthStart); setInputTo(today); load(monthStart, today); setFrom(monthStart); setTo(today); }}
            className="text-xs text-stone-400 hover:text-stone-600 underline">이번달</button>
        </div>
      </div>

      {/* 현재 조회 기간 표시 */}
      {!loading && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-sm text-amber-800 flex items-center gap-2">
          <span>📅</span>
          <strong>{periodLabel}</strong> 기간 데이터를 표시합니다.
          &nbsp;·&nbsp; 입고 <strong>{receipts.length}건</strong>
          &nbsp;·&nbsp; 출고 <strong>{shipments.length}건</strong>
        </div>
      )}

      {/* ── KPI ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="기간 입고 금액"  value={fmt(totalIn)}     sub={`${receipts.length}건`}   color="bg-green-50 text-green-800"   icon=""/>
        <KpiCard label="기간 출고 금액"  value={fmt(totalOut)}    sub={`${shipments.length}건`}  color="bg-blue-50 text-blue-800"     icon=""/>
        <KpiCard label="활성 거래처"     value={`${activeSupp}개`}  sub={`전체 ${suppliers.length}개`} color="bg-amber-50 text-amber-800" icon=""/>
        <KpiCard label="활성 사용자"     value={`${activeUsers}명`} sub={`전체 ${users.length}명`}   color="bg-purple-50 text-purple-800" icon=""/>
      </div>

      {/* ── 월별 추이 (최근 6개월 — 고정) ── */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <Section title="월별 입출고 금액 추이" sub="최근 6개월 고정 표시" />
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={monthlyData} margin={{top:5,right:20,left:10,bottom:5}}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4"/>
            <XAxis dataKey="month" tick={{fontSize:12}}/>
            <YAxis tick={{fontSize:11}} tickFormatter={v=>(v/10000).toFixed(0)+'만'}/>
            <Tooltip formatter={(v:any)=>fmt(Number(v))}/>
            <Legend/>
            <Bar dataKey="입고" fill="#22c55e" radius={[4,4,0,0]}/>
            <Bar dataKey="출고" fill="#3b82f6" radius={[4,4,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── 생산 현황 파이 ── */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <Section title="생산 현황 요약" sub={periodLabel}/>
          {prodStats.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={prodStats} dataKey="cnt" nameKey="status" cx="50%" cy="50%"
                  outerRadius={80} label={(props:any)=>`${PROD_LABELS[props.status]??props.status} ${props.cnt}`}>
                  {prodStats.map((e,i)=>(
                    <Cell key={i} fill={PROD_COLORS[e.status]??CHART_COLORS[i%CHART_COLORS.length]}/>
                  ))}
                </Pie>
                <Tooltip formatter={(v:any,name:any)=>[v, PROD_LABELS[name]??name]}/>
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-52 flex items-center justify-center text-stone-400 text-sm">해당 기간 생산 데이터 없음</div>
          )}
        </div>

        {/* ── 거래처별 입고 ── */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <Section title="거래처별 입고 금액" sub={periodLabel}/>
          {supplierChart.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={supplierChart} layout="vertical" margin={{left:20,right:30}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4"/>
                <XAxis type="number" tick={{fontSize:11}} tickFormatter={v=>(v/10000).toFixed(0)+'만'}/>
                <YAxis type="category" dataKey="name" tick={{fontSize:11}} width={80}/>
                <Tooltip formatter={(v:any)=>fmt(Number(v))}/>
                <Bar dataKey="amount" fill="#f59e0b" radius={[0,4,4,0]}/>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-52 flex items-center justify-center text-stone-400 text-sm">해당 기간 입고 데이터 없음</div>
          )}
        </div>
      </div>

      {/* ── 자재 입고 현황 ── */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <Section
          title={`자재 입고 현황 (${receipts.length}건 · 합계 ${fmt(totalIn)})`}
          sub={periodLabel}
          action={
            <button onClick={()=>downloadCSV('자재입고현황.csv',
              receipts.map(r=>[r.receipt_no,r.receipt_date,r.supplier_name,r.total_amount]),
              ['명세표번호','입고일자','공급업체','금액'])}
              className="text-xs bg-green-50 hover:bg-green-100 text-green-700 font-semibold px-3 py-1.5 rounded-lg transition flex items-center gap-1">
              📥 CSV
            </button>
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-100 text-xs font-semibold text-stone-500">
                <th className="text-left py-2 px-3">명세표 번호</th>
                <th className="text-left py-2 px-3">입고일자</th>
                <th className="text-left py-2 px-3">공급업체</th>
                <th className="text-right py-2 px-3">금액</th>
              </tr>
            </thead>
            <tbody>
              {receipts.length === 0 ? (
                <tr><td colSpan={4} className="text-center py-10 text-stone-400">해당 기간 입고 내역 없음</td></tr>
              ) : receipts.map(r=>(
                <tr key={r.id} className="border-b border-stone-50 hover:bg-stone-50 transition">
                  <td className="py-2.5 px-3 font-mono text-stone-600">{r.receipt_no}</td>
                  <td className="py-2.5 px-3 text-stone-600">{r.receipt_date}</td>
                  <td className="py-2.5 px-3 font-medium text-stone-800">{r.supplier_name}</td>
                  <td className="py-2.5 px-3 text-right font-semibold text-green-700">{fmt(r.total_amount)}</td>
                </tr>
              ))}
            </tbody>
            {receipts.length > 0 && (
              <tfoot>
                <tr className="bg-green-50 border-t-2 border-green-200">
                  <td colSpan={3} className="py-2.5 px-3 text-right font-bold text-stone-700">합계</td>
                  <td className="py-2.5 px-3 text-right font-bold text-green-700 text-base">{fmt(totalIn)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* ── 상품 출고 현황 ── */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <Section
          title={`상품 출고 현황 (${shipments.length}건 · 합계 ${fmt(totalOut)})`}
          sub={periodLabel}
          action={
            <button onClick={()=>downloadCSV('상품출고현황.csv',
              shipments.map(s=>[s.shipment_no,s.shipment_date,s.client_name,s.total_amount]),
              ['명세표번호','출고일자','거래처','금액'])}
              className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold px-3 py-1.5 rounded-lg transition flex items-center gap-1">
              📥 CSV
            </button>
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-100 text-xs font-semibold text-stone-500">
                <th className="text-left py-2 px-3">명세표 번호</th>
                <th className="text-left py-2 px-3">출고일자</th>
                <th className="text-left py-2 px-3">거래처</th>
                <th className="text-right py-2 px-3">금액</th>
              </tr>
            </thead>
            <tbody>
              {shipments.length === 0 ? (
                <tr><td colSpan={4} className="text-center py-10 text-stone-400">해당 기간 출고 내역 없음</td></tr>
              ) : shipments.map(s=>(
                <tr key={s.id} className="border-b border-stone-50 hover:bg-stone-50 transition">
                  <td className="py-2.5 px-3 font-mono text-stone-600">{s.shipment_no}</td>
                  <td className="py-2.5 px-3 text-stone-600">{s.shipment_date}</td>
                  <td className="py-2.5 px-3 font-medium text-stone-800">{s.client_name}</td>
                  <td className="py-2.5 px-3 text-right font-semibold text-blue-700">{fmt(s.total_amount)}</td>
                </tr>
              ))}
            </tbody>
            {shipments.length > 0 && (
              <tfoot>
                <tr className="bg-blue-50 border-t-2 border-blue-200">
                  <td colSpan={3} className="py-2.5 px-3 text-right font-bold text-stone-700">합계</td>
                  <td className="py-2.5 px-3 text-right font-bold text-blue-700 text-base">{fmt(totalOut)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* ── 가입자 현황 ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <Section title="파트너사별 가입자 현황"
            action={
              <button onClick={()=>downloadCSV('가입자현황.csv',
                users.map(u=>[u.name,u.email,u.partner_name,
                  u.role==='admin'?'관리자':u.role==='partner'?'파트너':'조회',
                  u.is_active?'활성':'비활성', u.last_login??'-']),
                ['이름','이메일','파트너사','권한','상태','마지막로그인'])}
                className="text-xs bg-purple-50 hover:bg-purple-100 text-purple-700 font-semibold px-3 py-1.5 rounded-lg transition flex items-center gap-1">
                📥 CSV
              </button>
            }
          />
          <div className="space-y-2">
            {partnerStats.map((p,i)=>(
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-stone-50">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                  style={{background: CHART_COLORS[i%CHART_COLORS.length]}}>
                  {p.name[0]}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-stone-700">{p.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 bg-stone-200 rounded-full h-1.5">
                      <div className="bg-green-500 h-1.5 rounded-full transition-all"
                        style={{width: p.total>0?`${(p.active/p.total)*100}%`:'0%'}}/>
                    </div>
                    <span className="text-xs text-stone-500">{p.active}/{p.total}명 활성</span>
                  </div>
                </div>
              </div>
            ))}
            {partnerStats.length === 0 && <p className="text-center text-stone-400 text-sm py-8">데이터 없음</p>}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-6">
          <Section title="전체 사용자 목록"/>
          <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
            {users.map(u=>(
              <div key={u.id} className={`flex items-center gap-3 p-2.5 rounded-lg ${!u.is_active?'opacity-40':'hover:bg-stone-50'}`}>
                <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center text-sm font-bold text-stone-600 flex-shrink-0">
                  {u.name[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-stone-700 truncate">{u.name}</p>
                  <p className="text-xs text-stone-400 truncate">{u.email}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    u.role==='admin'?'bg-red-100 text-red-700':'bg-amber-100 text-amber-700'}`}>
                    {u.role==='admin'?'관리자':u.role==='partner'?'파트너':'조회'}
                  </span>
                  <p className="text-xs text-stone-400 mt-0.5">
                    {u.last_login ? u.last_login.slice(0,10) : '미접속'}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-stone-100 flex justify-between text-xs text-stone-500">
            <span>전체 {users.length}명</span>
            <span>활성 {users.filter(u=>u.is_active).length}명 · 비활성 {users.filter(u=>!u.is_active).length}명</span>
          </div>
        </div>
      </div>

      {/* ── 거래처 현황 ── */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <Section title={`거래처 현황 (활성 ${activeSupp}개 / 전체 ${suppliers.length}개)`}
          action={
            <button onClick={()=>downloadCSV('거래처현황.csv',
              suppliers.map(s=>[s.company_name,s.is_active?'활성':'비활성']),['회사명','상태'])}
              className="text-xs bg-amber-50 hover:bg-amber-100 text-amber-700 font-semibold px-3 py-1.5 rounded-lg transition flex items-center gap-1">
              📥 CSV
            </button>
          }
        />
        <div className="flex flex-wrap gap-2">
          {suppliers.map(s=>(
            <span key={s.id} className={`text-sm px-3 py-1.5 rounded-full font-medium ${
              s.is_active?'bg-amber-100 text-amber-800':'bg-stone-100 text-stone-400 line-through'}`}>
              {s.company_name}
            </span>
          ))}
          {suppliers.length===0 && <p className="text-stone-400 text-sm">등록된 거래처 없음</p>}
        </div>
      </div>

      {/* ── 전체 CSV ── */}
      <div className="bg-stone-900 text-white rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-bold">전체 데이터 내보내기</p>
          <p className="text-stone-400 text-sm mt-0.5">현재 조회 기간 기준으로 다운로드</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            {label:'입고 내역', fn:()=>downloadCSV('입고내역.csv',receipts.map(r=>[r.receipt_no,r.receipt_date,r.supplier_name,r.total_amount]),['번호','일자','공급업체','금액'])},
            {label:'출고 내역', fn:()=>downloadCSV('출고내역.csv',shipments.map(s=>[s.shipment_no,s.shipment_date,s.client_name,s.total_amount]),['번호','일자','거래처','금액'])},
            {label:'사용자 목록', fn:()=>downloadCSV('사용자목록.csv',users.map(u=>[u.name,u.email,u.partner_name,u.role,u.is_active?'활성':'비활성']),['이름','이메일','파트너','권한','상태'])},
            {label:'거래처 목록', fn:()=>downloadCSV('거래처목록.csv',suppliers.map(s=>[s.company_name,s.is_active?'활성':'비활성']),['회사명','상태'])},
          ].map(btn=>(
            <button key={btn.label} onClick={btn.fn}
              className="bg-stone-700 hover:bg-amber-500 hover:text-black text-white text-sm font-semibold px-4 py-2 rounded-lg transition">
              📥 {btn.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
